import React from 'react';
import { ICONS_MAP } from '../../utils/constants';
import { useLanguage } from '../../contexts/LanguageContext';

// Icon component
const Icon = ({ path, className = 'w-6 h-6' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
        <path d={path} />
    </svg>
);

const CategoryTabs = React.memo(({ currentStatus, setActiveTab, ordersByStatus, statusConfig, isMobile, totalRevenue, visibleStatuses }) => {
    const { t, isRTL } = useLanguage();
    if (!visibleStatuses) visibleStatuses = Object.keys(statusConfig);

    // Get pre-calculated counts from the special property
    const counts = ordersByStatus?._counts || {};

    // Tailwind Safelist mapping to ensure classes are generated
    const STATUS_THEMES = {
        blue: {
            active: 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-md scale-105',
            inactive: 'bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-100 hover:shadow-sm',
            badgeActive: 'bg-white/20 text-white',
            badgeInactive: 'bg-blue-200 text-blue-800',
            iconActive: 'bg-white/20',
            iconInactive: 'bg-blue-100 text-blue-600',
            ring: 'focus:ring-blue-500'
        },
        purple: {
            active: 'bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-md scale-105',
            inactive: 'bg-purple-50 text-purple-700 border border-purple-100 hover:bg-purple-100 hover:shadow-sm',
            badgeActive: 'bg-white/20 text-white',
            badgeInactive: 'bg-purple-200 text-purple-800',
            iconActive: 'bg-white/20',
            iconInactive: 'bg-purple-100 text-purple-600',
            ring: 'focus:ring-purple-500'
        },
        yellow: {
            active: 'bg-gradient-to-br from-yellow-500 to-yellow-600 text-white shadow-md scale-105',
            inactive: 'bg-yellow-50 text-yellow-700 border border-yellow-100 hover:bg-yellow-100 hover:shadow-sm',
            badgeActive: 'bg-white/20 text-white',
            badgeInactive: 'bg-yellow-200 text-yellow-800',
            iconActive: 'bg-white/20',
            iconInactive: 'bg-yellow-100 text-yellow-600',
            ring: 'focus:ring-yellow-500'
        },
        green: {
            active: 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-md scale-105',
            inactive: 'bg-green-50 text-green-700 border border-green-100 hover:bg-green-100 hover:shadow-sm',
            badgeActive: 'bg-white/20 text-white',
            badgeInactive: 'bg-green-200 text-green-800',
            iconActive: 'bg-white/20',
            iconInactive: 'bg-green-100 text-green-600',
            ring: 'focus:ring-green-500'
        },
        red: {
            active: 'bg-gradient-to-br from-red-500 to-red-600 text-white shadow-md scale-105',
            inactive: 'bg-red-50 text-red-700 border border-red-100 hover:bg-red-100 hover:shadow-sm',
            badgeActive: 'bg-white/20 text-white',
            badgeInactive: 'bg-red-200 text-red-800',
            iconActive: 'bg-white/20',
            iconInactive: 'bg-red-100 text-red-600',
            ring: 'focus:ring-red-500'
        },
        orange: {
            active: 'bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-md scale-105',
            inactive: 'bg-orange-50 text-orange-700 border border-orange-100 hover:bg-orange-100 hover:shadow-sm',
            badgeActive: 'bg-white/20 text-white',
            badgeInactive: 'bg-orange-200 text-orange-800',
            iconActive: 'bg-white/20',
            iconInactive: 'bg-orange-100 text-orange-600',
            ring: 'focus:ring-orange-500'
        },
        gray: {
            active: 'bg-gradient-to-br from-gray-500 to-gray-600 text-white shadow-md scale-105',
            inactive: 'bg-gray-50 text-gray-700 border border-gray-100 hover:bg-gray-100 hover:shadow-sm',
            badgeActive: 'bg-white/20 text-white',
            badgeInactive: 'bg-gray-200 text-gray-800',
            iconActive: 'bg-white/20',
            iconInactive: 'bg-gray-100 text-gray-600',
            ring: 'focus:ring-gray-500'
        },
        indigo: {
            active: 'bg-gradient-to-br from-indigo-500 to-indigo-600 text-white shadow-md scale-105',
            inactive: 'bg-indigo-50 text-indigo-700 border border-indigo-100 hover:bg-indigo-100 hover:shadow-sm',
            badgeActive: 'bg-white/20 text-white',
            badgeInactive: 'bg-indigo-200 text-indigo-800',
            iconActive: 'bg-white/20',
            iconInactive: 'bg-indigo-100 text-indigo-600',
            ring: 'focus:ring-indigo-500'
        }
    };

    const getColorClasses = (color, isActive, isBadge = false, isIcon = false) => {
        const c = STATUS_THEMES[color] || STATUS_THEMES.indigo;
        if (isBadge) return isActive ? c.badgeActive : c.badgeInactive;
        if (isIcon) return isActive ? c.iconActive : c.iconInactive;
        return isActive ? c.active : c.inactive;
    };

    const renderTabs = (mobile = false) => {
        return visibleStatuses.map(status => {
            const config = statusConfig[status];
            const count = counts[status] || 0;
            const isActive = currentStatus === status;
            const addSpacing = !mobile && status === 'cancelled';
            const color = config.color || 'indigo';
            
            return (
                <button
                    key={status}
                    onClick={() => setActiveTab(status)}
                    className={`group relative px-2.5 py-1.5 rounded-xl font-semibold transition-all duration-300 ease-in-out flex-shrink-0 flex items-center gap-1.5
                        ${addSpacing ? (isRTL ? 'mr-4' : 'ml-4') : ''}
                        ${getColorClasses(color, isActive)}
                        focus:outline-none ${!mobile ? `focus:ring-2 ${STATUS_THEMES[color]?.ring || 'focus:ring-indigo-500'} focus:ring-offset-1` : ''}`}
                >
                    {/* Icon */}
                    <div className={`p-1 rounded-lg transition-colors ${getColorClasses(color, isActive, false, true)}`}>
                        <Icon path={ICONS_MAP[config.icon]} className="w-3.5 h-3.5" />
                    </div>
                    {/* Status name */}
                    <span className={`text-xs tracking-wide capitalize ${isActive ? 'font-bold' : 'font-semibold'}`}>{t(status)}</span>
                    {/* Count badge */}
                    <div className={`min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center text-[10px] font-bold ${getColorClasses(color, isActive, true)}`}>
                        {count}
                    </div>
                </button>
            );
        });
    };

    if (isMobile) {
        return (
            <div className="sticky -top-4 z-20 bg-gray-50/95 backdrop-blur-sm py-1.5 -mx-4 mb-3 -mt-0">
                <div className={`flex overflow-x-auto gap-2 py-1 w-full [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ${isRTL ? 'pr-4' : 'pl-4'}`}>
                    {renderTabs(true)}
                    <div className="flex-shrink-0 w-4"></div>
                </div>
            </div>
        );
    }

    // Desktop version
    return (
        <div className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur-sm py-3 -mx-1 px-1 mb-4">
            <div className="flex flex-wrap gap-2">
                {renderTabs(false)}
            </div>
        </div>
    );
});

export default CategoryTabs;